import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { User, Shield, KeyRound, Mail, Save, Lock } from 'lucide-react';

export default function MyProfile() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    full_name: '', phone: '', job_title: '', email_signature: '',
    address_street: '', address_city: '', address_state: '', address_zip: '',
    emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    api.get('/auth/me').then(res => {
      if (!active) return;
      const d = res.data || {};
      setForm(prev => ({
        ...prev,
        full_name: d.full_name || '', phone: d.phone || '', job_title: d.job_title || '',
        email_signature: d.email_signature || '',
        address_street: d.address_street || '', address_city: d.address_city || '',
        address_state: d.address_state || '', address_zip: d.address_zip || '',
        emergency_contact_name: d.emergency_contact_name || '',
        emergency_contact_phone: d.emergency_contact_phone || '',
        emergency_contact_relationship: d.emergency_contact_relationship || '',
      }));
    }).catch(() => toast.error('Failed to load your profile')).finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/auth/me/profile', form);
      await refreshUser();
      toast.success('Profile updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="co-page co-scope co-settings space-y-6" data-testid="my-profile-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="my-profile-title">
          <User className="h-6 w-6" /> My Profile
        </h1>
        <p className="text-sm opacity-80">Update your own contact details and email signature. Account email and role are managed by an administrator.</p>
      </div>

      {/* Identity (read-only) */}
      <Card data-testid="my-profile-identity">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" /> Account</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Email</Label>
            <Input value={user?.email || ''} disabled data-testid="my-profile-email" />
            <p className="text-xs opacity-60 mt-1">Changed by an administrator only.</p>
          </div>
          <div>
            <Label>Role</Label>
            <Input value={user?.role_name || user?.role || ''} disabled data-testid="my-profile-role" />
            <p className="text-xs opacity-60 mt-1">Assigned by an administrator.</p>
          </div>
        </CardContent>
      </Card>

      {/* Editable own fields */}
      <Card data-testid="my-profile-details">
        <CardHeader>
          <CardTitle className="text-base">Contact details</CardTitle>
          <CardDescription>These are your own details and are safe for you to edit.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Full name</Label>
            <Input value={form.full_name} onChange={set('full_name')} data-testid="my-profile-full-name" />
          </div>
          <div>
            <Label>Job title</Label>
            <Input value={form.job_title} onChange={set('job_title')} data-testid="my-profile-job-title" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.phone} onChange={set('phone')} data-testid="my-profile-phone" />
          </div>
          <div className="sm:col-span-2"><Separator /></div>
          <div className="sm:col-span-2">
            <Label>Street address</Label>
            <Input value={form.address_street} onChange={set('address_street')} data-testid="my-profile-street" />
          </div>
          <div>
            <Label>City</Label>
            <Input value={form.address_city} onChange={set('address_city')} data-testid="my-profile-city" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>State</Label>
              <Input value={form.address_state} onChange={set('address_state')} data-testid="my-profile-state" />
            </div>
            <div>
              <Label>ZIP</Label>
              <Input value={form.address_zip} onChange={set('address_zip')} data-testid="my-profile-zip" />
            </div>
          </div>
          <div className="sm:col-span-2"><Separator /></div>
          <div>
            <Label>Emergency contact name</Label>
            <Input value={form.emergency_contact_name} onChange={set('emergency_contact_name')} data-testid="my-profile-ec-name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Emergency phone</Label>
              <Input value={form.emergency_contact_phone} onChange={set('emergency_contact_phone')} data-testid="my-profile-ec-phone" />
            </div>
            <div>
              <Label>Relationship</Label>
              <Input value={form.emergency_contact_relationship} onChange={set('emergency_contact_relationship')} data-testid="my-profile-ec-rel" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email signature */}
      <Card data-testid="my-profile-signature-card">
        <CardHeader>
          <CardTitle className="text-base">Email signature</CardTitle>
          <CardDescription>Appended when you reply to tickets and send email on your behalf.</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={5}
            value={form.email_signature}
            onChange={set('email_signature')}
            placeholder={'e.g.\nJane Doe\nSenior Engineer\nCipherOne'}
            data-testid="my-profile-signature"
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving || loading} data-testid="my-profile-save-btn">
          <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>

      {/* Security: SSO / MFA */}
      <Card data-testid="my-profile-security">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Shield className="h-4 w-4" /> Sign-in & security</CardTitle>
          <CardDescription>Manage how you sign in and protect your account.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-3">
          <Button variant="outline" onClick={() => navigate('/mfa-settings')} data-testid="my-profile-mfa-link">
            <KeyRound className="h-4 w-4 mr-2" /> Two-factor (MFA) settings
          </Button>
          <Button variant="outline" onClick={() => navigate('/office365')} data-testid="my-profile-sso-link">
            <Mail className="h-4 w-4 mr-2" /> Microsoft 365 single sign-on
          </Button>
        </CardContent>
      </Card>

      {/* Vault help */}
      <Card data-testid="my-profile-vault-help">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Lock className="h-4 w-4" /> Credential vault — how reveal works</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2 opacity-90">
          <p>Revealing a stored credential password asks you to confirm your identity. You can enter either your normal sign-in password or, if you have set one, a separate vault password.</p>
          <p>Access to reveal vault passwords is restricted to administrators. If you cannot reveal a credential, your role does not currently grant vault access — contact an administrator.</p>
          <p className="opacity-70">Your sign-in password and MFA are changed from the Sign-in &amp; security options above, not here.</p>
        </CardContent>
      </Card>
    </div>
  );
}
